// src/features/estoque/components/ModalMapeamentoNota.jsx
import React, { useState, useEffect } from "react";
import { salvarMapeamento } from "services/estoqueService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  AlertTriangle,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import toast from "react-hot-toast";

export const ModalMapeamentoNota = ({
  isOpen,
  onClose,
  notaInfo = {},
  items = [],
  estoqueItems = [],
  onSalvarSucesso,
}) => {
  // Estado local para armazenar seleção de produto interno por item
  // [codigoFornecedor]: { codigoInterno, categoriaContabil, fatorConversao }
  const [mapeamentosState, setMapeamentosState] = useState({});
  const [salvando, setSalvando] = useState(false);

  // Inicializa o estado sempre que os itens mudarem
  useEffect(() => {
    if (!items || items.length === 0) return;

    const inicial = {};
    items.forEach((it) => {
      // Sugestão automática: se existir item no estoque com nome parecido ou código parecido
      const sugestaoEstoque = estoqueItems.find((e) =>
        e.nome.toLowerCase().includes(it.nomeFornecedor.toLowerCase().slice(0, 8))
      );

      inicial[it.codigoFornecedor] = {
        codigoInterno: sugestaoEstoque ? sugestaoEstoque.codigo : "",
        categoriaContabil: "REVENDA",
        fatorConversao: it.fatorConversaoSugerido || 1,
      };
    });

    setMapeamentosState(inicial);
  }, [items, estoqueItems]);

  const handleChangeItem = (codForn, campo, valor) => {
    setMapeamentosState((prev) => ({
      ...prev,
      [codForn]: {
        ...prev[codForn],
        [campo]: valor,
      },
    }));
  };

  const handleSalvar = async () => {
    // Validar se todos os itens foram mapeados
    for (const it of items) {
      const config = mapeamentosState[it.codigoFornecedor];
      if (!config || !config.codigoInterno) {
        toast.error(`Selecione o produto do estoque para "${it.nomeFornecedor}".`);
        return;
      }
      const fatorNum = Number(config.fatorConversao);
      if (!fatorNum || fatorNum <= 0) {
        toast.error(`O fator de conversão de "${it.nomeFornecedor}" deve ser maior que zero.`);
        return;
      }
    }

    setSalvando(true);
    try {
      // Salva cada mapeamento no banco de dados
      for (const it of items) {
        const config = mapeamentosState[it.codigoFornecedor];
        const isDespesa = config.codigoInterno === "DESP_GERAL";
        const categoria = isDespesa ? "INSUMO" : (config.categoriaContabil || "REVENDA");
        const fator = isDespesa ? 1 : Number(config.fatorConversao || 1);

        await salvarMapeamento(
          it.cnpjEmitente || notaInfo.cnpjEmitente,
          it.codigoFornecedor,
          config.codigoInterno,
          categoria,
          fator,
          it.nomeFornecedor,
          it.uCom || "UN"
        );
      }

      toast.success("Mapeamentos salvos com sucesso!", { icon: "🏷️" });

      if (onSalvarSucesso) {
        await onSalvarSucesso(mapeamentosState);
      }
      onClose();
    } catch (err) {
      console.error("Erro ao salvar mapeamento:", err);
      toast.error(`Falha ao salvar mapeamento: ${err.message}`);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0 overflow-hidden bg-slate-900 text-slate-100 border-slate-800 shadow-2xl">
        <DialogHeader className="p-4 sm:p-6 pb-3 border-b border-slate-800 bg-slate-950/70">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-100 flex items-center gap-2">
                Mapeamento de Bebidas Pendentes
                <span className="text-xs font-mono bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/30">
                  {items.length} {items.length === 1 ? "item" : "itens"}
                </span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-400 mt-0.5">
                {notaInfo.numero ? `NF-e nº ${notaInfo.numero} • ` : ""}
                Vincule cada bebida ao seu produto correspondente no estoque e confirme o fator de conversão de embalagem.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* LISTA DE PRODUTOS PENDENTES */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 pr-2">
          {items.map((it, idx) => {
            const config = mapeamentosState[it.codigoFornecedor] || {};
            const isDespesa = config.codigoInterno === "DESP_GERAL";
            const fator = Number(config.fatorConversao) || 1;
            const qtdNota = it.qCom || it.quantidade || 1;
            const unidadesCalculadas = Math.round(qtdNota * fator);

            return (
              <div
                key={`${it.codigoFornecedor}-${idx}`}
                className="bg-slate-950/70 border border-slate-800 rounded-xl p-3.5 sm:p-4 space-y-3 transition-colors hover:border-slate-700"
              >
                {/* Cabeçalho do Produto da Nota */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-[10px] font-mono text-slate-500 block">
                      Cód. Fornecedor: {it.codigoFornecedor} • NCM: {it.ncm || "2202"}
                    </span>
                    <h4 className="font-bold text-sm text-slate-100 leading-tight truncate mt-0.5">
                      {it.nomeFornecedor}
                    </h4>
                  </div>

                  <span className="bg-slate-800 text-slate-300 font-mono text-xs px-2 py-0.5 rounded border border-slate-700 shrink-0">
                    Unidade: <strong>{it.uCom || "UN"}</strong>
                  </span>
                </div>

                {/* Seleção de Item do Estoque */}
                <div className="space-y-1.5 pt-1">
                  <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                    <ArrowRight className="w-3.5 h-3.5 text-emerald-400" />
                    Vincular ao Produto no Parque:
                  </label>
                  <select
                    value={config.codigoInterno || ""}
                    onChange={(e) =>
                      handleChangeItem(it.codigoFornecedor, "codigoInterno", e.target.value)
                    }
                    className="w-full text-xs sm:text-sm bg-slate-900 border border-slate-700 text-slate-100 rounded-lg p-2.5 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  >
                    <option value="" disabled>
                      --- Selecione o produto correspondente ---
                    </option>
                    <option value="DESP_GERAL">
                      ❌ Não controlar no estoque (Despesa Geral / Consumo Interno)
                    </option>
                    {estoqueItems.map((est) => {
                      const qtd = Number(est.quantidadeAtual || 0).toLocaleString("pt-BR");
                      return (
                        <option key={est.codigo} value={est.codigo}>
                          {est.nome} (Cód: {est.codigo} • Estoque: {qtd} un)
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Opções Avançadas de Conversão (Se não for despesa geral) */}
                {!isDespesa && config.codigoInterno && (
                  <div className="pt-2 border-t border-slate-800/80 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-900/60 p-3 rounded-lg">
                    {/* Categoria Contábil */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-slate-400 block">
                        Destino Contábil:
                      </label>
                      <select
                        value={config.categoriaContabil || "REVENDA"}
                        onChange={(e) =>
                          handleChangeItem(it.codigoFornecedor, "categoriaContabil", e.target.value)
                        }
                        className="w-full text-xs bg-slate-950 border border-slate-800 text-slate-200 rounded-md p-1.5"
                      >
                        <option value="REVENDA">🏷️ REVENDA (Soma ao estoque físico do Bar)</option>
                        <option value="INSUMO">📦 INSUMO (Consumo / Não altera estoque de venda)</option>
                      </select>
                    </div>

                    {/* Fator de Conversão */}
                    <div className="space-y-1">
                      <label className="text-[11px] font-medium text-slate-400 flex items-center justify-between">
                        <span>Fator de Conversão:</span>
                        <span className="text-amber-400 font-bold font-mono">
                          {fator} un/{it.uCom || "EB"}
                        </span>
                      </label>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-slate-500">1 {it.uCom || "EB"} =</span>
                        <Input
                          type="number"
                          min="1"
                          max="1000"
                          value={config.fatorConversao || 1}
                          onChange={(e) =>
                            handleChangeItem(it.codigoFornecedor, "fatorConversao", e.target.value)
                          }
                          className="h-8 text-xs bg-slate-950 border-slate-800 text-amber-300 font-bold text-center w-20"
                        />
                        <span className="text-xs text-slate-500">unidades</span>
                      </div>
                    </div>

                    {/* Preview da Entrada em Unidades */}
                    <div className="col-span-1 sm:col-span-2 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 p-2 rounded-md flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        Resultado: Nota tem <strong>{qtdNota} {it.uCom || "embalagens"}</strong> $\times$ <strong>{fator}</strong> = o estoque será incrementado em <strong className="underline text-emerald-300">+{unidadesCalculadas} latas/unidades</strong>.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* RODAPÉ COM AÇÕES */}
        <DialogFooter className="p-4 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between sm:justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={salvando} className="text-xs h-9">
            Cancelar
          </Button>
          <Button
            onClick={handleSalvar}
            disabled={salvando}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs h-9 px-4 shadow-lg shadow-emerald-500/10"
          >
            {salvando ? "Salvando Mapeamentos..." : "Salvar Mapeamento e Atualizar Estoque"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalMapeamentoNota;
